import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { MaskLines } from "./primitives";
import { APP_LINKS, HERO_IMG, scrollToId } from "../../lib/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax: background drifts slower, content lifts away
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-navy"
      data-testid="hero-section"
    >
      {/* Living cinematic background (image treated as looping video) */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_IMG}
          alt="Golden hour rooftop solar in India"
          className="hero-kenburns h-[115%] w-full object-cover"
        />
        {/* moving sun glare to simulate live footage */}
        <div className="sun-drift absolute -top-1/4 right-[6%] h-[60vh] w-[60vh] rounded-full bg-[radial-gradient(circle,rgba(255,196,120,0.55)_0%,rgba(255,150,60,0.18)_38%,transparent_70%)] blur-2xl" />
      </motion.div>

      {/* gradient legibility overlays */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-navy via-navy/25 to-navy/40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-transparent to-transparent" />

      {/* content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-9 sm:pb-[9vh]"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="eyebrow text-amber-400 mb-5"
        >
          SunShell One — Own Your Power
        </motion.p>

        <h1 className="display-serif text-white font-medium leading-[1.02] tracking-[-0.02em] text-[15vw] sm:text-[10vw] lg:text-[7.4rem] max-w-[16ch]">
          <MaskLines
            lines={["India's first", "Home Energy OS."]}
            delay={0.4}
            lineClass="pb-[0.16em]"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-5 max-w-[46ch] rounded-3xl border border-white/10 bg-navy/28 px-5 py-4 text-lg leading-relaxed text-white shadow-2xl shadow-navy/20 backdrop-blur-md sm:mt-6 sm:text-xl"
        >
          One app that runs your entire home solar plant — generation, savings,
          service, everything.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-7 flex w-full flex-col items-stretch gap-3 sm:mt-9 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4"
        >
          <a
            href={APP_LINKS.customer.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-customer-app-btn"
            className="btn-pulse group inline-flex items-center justify-center gap-2 rounded-full bg-amber px-8 py-4 text-base font-semibold text-white shadow-xl shadow-amber/30 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-amber-600 sm:justify-start"
          >
            Download customer app
            <ArrowUpRight size={19} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            onClick={() => scrollToId("problem")}
            data-testid="hero-seehow-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-4 text-base font-medium text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/12 sm:justify-start"
          >
            See how it works
          </button>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.button
        onClick={() => scrollToId("problem")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/70 sm:flex"
        aria-label="Scroll down"
        data-testid="hero-scroll-cue"
      >
        <span className="eyebrow text-[0.62rem]">Scroll</span>
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ChevronDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
};
